import { InputField } from '@/components/CommonComponents/InputField';
import SurveyPreviewModal from '@/components/CommonComponents/SurveyPreviewModal';
import { convertExcelToSurveyJson } from '@/helper/excelConverter.helper';
import { extractFileNameFromUrl } from '@/helper/utils';
import { SurveyListItem } from '@/types/survey.types';
import { FileText, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

type BasicInfoProps = {
  survey: Partial<SurveyListItem>;
  onSurveyChange: (survey: Partial<SurveyListItem>) => void;
  onFileData: (file: File | null) => void;
  onDownloadFile: () => void;
  isDownloading: boolean;
  onPreview: () => void;
  error: Record<string, string>;
  setError: (error: Record<string, string>) => void;
};

const BasicInfo: React.FC<BasicInfoProps> = ({
  survey,
  onSurveyChange,
  onFileData,
  onDownloadFile,
  isDownloading,
  onPreview,
  error,
  setError,
}) => {
  const location = useLocation();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewSurvey, setPreviewSurvey] = useState<SurveyListItem | null>(null);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const isEditMode = !!survey.id;

  const existingFileName = useMemo(() => {
    if (isEditMode && survey.odkFileUrl) {
      return extractFileNameFromUrl(survey.odkFileUrl);
    }
    return null;
  }, [isEditMode, survey.odkFileUrl]);

  const handleFileDataCallback = useCallback(
    (fileData: File | null) => {
      onFileData(fileData);
    },
    [onFileData],
  );

  useEffect(() => {
    // Initialize survey data from navigation state if available
    const navigationSurvey = location.state?.survey;
    if (navigationSurvey) {
      onSurveyChange(navigationSurvey);
    }
  }, [location.state, onSurveyChange]);

  useEffect(() => {
    if (survey.fileData && !file) {
      const buffer = Uint8Array.from(atob(survey.fileData.content), (c) => c.charCodeAt(0));
      const reconstructedFile = new File([buffer], survey.fileData.name, {
        type: survey.fileData.type,
      });
      setFile(reconstructedFile);
      handleFileDataCallback(reconstructedFile);
    } else if (!survey.fileData && file) {
      setFile(null);
      handleFileDataCallback(null);
    }
  }, [survey.fileData, file, handleFileDataCallback]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const uploadedFile = e.target.files[0];
      processFile(uploadedFile);
    }
  };

  const processFile = (uploadedFile: File) => {
    setFileError('');

    const validExtensions = ['.xlsx', '.xml', '.xls'];
    const fileExtension = uploadedFile.name
      .substring(uploadedFile.name.lastIndexOf('.'))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setFileError('Invalid file format. Supported formats: .xlsx, .xls, .xml (Max size: 20MB)');
      return;
    }

    if (uploadedFile.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds the allowed limit. File must be 20MB or less.');
      return;
    }

    setFile(uploadedFile);
    handleFileDataCallback(uploadedFile);

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = btoa(
        new Uint8Array(reader.result as ArrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          '',
        ),
      );

      onSurveyChange({
        ...survey,
        fileData: {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          content: base64,
        },
      });
    };
    reader.readAsArrayBuffer(uploadedFile);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      processFile(droppedFile);
    }
  };

  const handlePreviewFile = async () => {
    if (!file && !survey.questionsJson) return;

    try {
      let surveyToPreview: SurveyListItem;

      if (file) {
        const { questions, languages, defaultLanguage } = await convertExcelToSurveyJson(file);
        surveyToPreview = {
          id: 0,
          title: file.name.replace(/\.[^/.]+$/, ''),
          odkFileUrl: '',
          questionsJson: questions,
          surveySets: [],
          status: true,
          createdAt: new Date().toISOString(),
          createdBy: null,
          languages: languages.map((lang, index) => ({
            id: index + 1,
            name: lang.name,
            code: lang.code,
          })),
          defaultLanguageId: defaultLanguage
            ? languages.findIndex((l) => l.value === defaultLanguage) + 1
            : 1,
        };
      } else {
        // Use existing survey data for preview with proper default language handling
        const defaultLanguage = survey.languages?.find(
          (lang) => lang.id === survey.defaultLanguageId,
        );
        surveyToPreview = {
          ...survey,
          languages: survey.languages || [],
          defaultLanguageId: survey.defaultLanguageId || 1,
          questionsJson:
            survey.questionsJson?.map((question) => ({
              ...question,
              defaultLanguage: defaultLanguage?.name || 'English (en)',
            })) || [],
        } as SurveyListItem;
      }

      setPreviewSurvey(surveyToPreview);
      setShowPreview(true);
    } catch (error) {
      console.error('Error converting file:', error);
      setFileError('Failed to convert Excel file. Please check the format.');
    }
  };

  const odkFileUrlDecode = survey.odkFileUrl;
  const fileName = odkFileUrlDecode?.split('_').slice(1).join('_');
  const cleanName = fileName?.replace(/^\d+_/, '');

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/MillionMakers_Survey_sample_file.xlsx';
    link.download = 'MillionMakers_Survey_sample_file.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateTitle = (value: string) => {
    const trimmed = value.trim();

    if (!trimmed) {
      setError({ title: 'Please enter a survey title' });
      return;
    }

    if (trimmed.length < 5 || trimmed.length > 60) {
      setError({ title: 'Title must be 5-60 characters' });
      return;
    }

    // clear title-related errors
    if (error.title) {
      setError({});
    }
  };

  return (
    <div className="mx-auto text-[#621C43]">
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">Basic Information</h2>
        <p className="text-base font-medium">
          {isEditMode
            ? 'Enter the basic details for the survey'
            : 'Enter the basic details for the new survey'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 px-6 space-y-3 py-2">
        <InputField
          label="Survey Title"
          required
          value={survey.title || ''}
          onChange={(e) => {
            const value = e.target.value;
            onSurveyChange({ ...survey, title: value });
            validateTitle(value);
          }}
          placeholder="Enter Survey Title"
        />
        {error && error.title && (
          <p className="text-sm text-red-500 font-medium mt-1">{error.title}</p>
        )}

        <div className="mb-4">
          <label className="mb-1 text-sm text-[#621C43] font-medium">
            Upload Survey File <span className="text-red-500">*</span>
          </label>

          {/* Template Download Section */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="text-blue-600" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Download ODK Sample Template</p>
                <p className="text-xs text-blue-600">
                  Use our template to ensure your file has the correct format
                </p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
              >
                Download Template
              </button>
            </div>
          </div>

          <div
            className={`rounded-lg border-2 bg-[#4A082D] bg-opacity-20 flex flex-col items-center justify-center p-6 text-center transition-all h-[200px] ${
              isDragging ? 'border-pink-600 bg-pink-50' : 'border-dashed'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isEditMode || file ? (
              <FileText className="text-[#621C43] mb-3" size={32} />
            ) : (
              <Upload className="text-[#621C43] mb-3" size={32} />
            )}

            {isEditMode ? (
              <p className="text-[14px] text-[#0BA96F] font-medium">{cleanName}</p>
            ) : (
              ''
            )}
            {file || existingFileName ? (
              <p className="text-[14px] text-[#0BA96F] font-medium mb-4">{file?.name}</p>
            ) : (
              <div className="mb-4">
                <p className="text-[14px] text-[#621C43] font-medium mb-2">
                  Drag & drop your file here or click to browse
                </p>
                <p className="text-[12px] text-[#621C43]">
                  Supported formats: .xlsx, .xls, .xml (Max size: 20MB)
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={file ? handlePreviewFile : onPreview}
                disabled={!file && !existingFileName && !survey.questionsJson}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition ${
                  file || existingFileName || survey.questionsJson
                    ? 'bg-[#0BA96F] text-white hover:bg-[#08965f]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Preview
              </button>

              <input
                type="file"
                accept=".xlsx,.xml,.xls"
                className="hidden"
                id="file-upload"
                onChange={handleFileChange}
              />

              <label
                htmlFor="file-upload"
                className="px-6 py-2 bg-[#4C86E7] text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-[#3b74d1] transition"
              >
                Choose File
              </label>
            </div>
          </div>

          {fileError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">{fileError}</p>
            </div>
          )}
        </div>
      </div>

      <SurveyPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        survey={previewSurvey}
      />
    </div>
  );
};

export default BasicInfo;
